/**
 * Initialize a physically based three.js renderer with a standard configuration.
 * @module
 */
import { assertIsNotNull, assertIsNotUndefined } from "common/utils/assertOfType";
import { productLog } from "common/utils/logging";
import noop from "common/utils/noop";
import * as THREE from "three";
import { SITE_CAMERA_FADE_LAYER, UI_LAYER } from "./layerConstants";
import { safeShaderReplace } from "./materials/shaderUtils";
import { RenderTargetPool } from "./threejs/higharc/RenderTargetPool";
import { SSFXLightweightRenderTarget } from "./threejs/higharc/SSFXLightweightRenderTarget";
import type { DefaultRenderParamOverrides } from "./threejs/higharc/SSFXRendererParams";
import { SSFXRendererParamsManager } from "./threejs/higharc/SSFXRendererParams";
import { SSFXRenderTarget } from "./threejs/higharc/SSFXRenderTarget";
import type { ExtendedWebGLRenderer } from "./threejs/three-extensions";
import {
  TONEMAPPING_CYAN_HIGHLIGHTS_FACTOR,
  TONEMAPPING_PURPLE_HIGHLIGHTS_FACTOR,
  TONEMAPPING_YELLOW_HIGHLIGHTS_FACTOR,
} from "./toneMappingConstants";
import type { WebGLExtendedCapabilities } from "./WebGLExtendedCapabilities";

export interface RendererInitOptions {
  ssfxRenderer: boolean;
  useMSAA: boolean;
}

const defaultOptions: RendererInitOptions = {
  ssfxRenderer: true,
  useMSAA: false,
};

export interface InitializedRenderer {
  renderer: THREE.WebGLRenderer;
  screenRenderTarget?: SSFXRenderTarget;
  screenLightweightRenderTarget?: SSFXLightweightRenderTarget;
  ssfxParamsManager?: SSFXRendererParamsManager;
  renderTargetPool: RenderTargetPool;
}

function reorderFogAndColorSpace(chunkName: string, shader: { fragmentShader: string }) {
  if (
    THREE.ShaderChunk[chunkName].indexOf("<fog_fragment>") >
    THREE.ShaderChunk[chunkName].indexOf("<tonemapping_fragment>")
  ) {
    // Applying fog should happen before colorspace.
    THREE.ShaderChunk[chunkName] = safeShaderReplace(
      THREE.ShaderChunk[chunkName],
      /#include\s*<tonemapping_fragment>\s*#include\s*<colorspace_fragment>\s*#include\s*<fog_fragment>/,
      `
// Note: fog/tonemapping/colorspace order changed compared to upstream three.js!
#include <fog_fragment>
#include <tonemapping_fragment>
#include <colorspace_fragment>
`,
      `reorderFogAndColorSpace ${chunkName}`,
    );
    shader.fragmentShader = THREE.ShaderChunk[chunkName];
  }
}

type ProgramMarker = { id: number; shaderName: string | undefined };
const internalProgramMap = new Map<WebGLProgram, ProgramMarker>();
let programIdCounter = 0;

function getShaderName(program: THREE.WebGLProgram): string {
  if (program.name) {
    return program.name;
  }

  const type = (program as any).type ?? "type";
  return `unknown ${type} shader`;
}

const logShaders = false; // update this to log linked shader programs

function enableShaderLogging(renderer: THREE.WebGLRenderer, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
  if (!logShaders) {
    return;
  }

  const oldLinkProgram = gl.linkProgram;
  gl.linkProgram = (program: WebGLProgram) => {
    if (!internalProgramMap.has(program)) {
      programIdCounter++;
      internalProgramMap.set(program, { id: programIdCounter, shaderName: undefined });
      performance.mark("start-compile-webgl-program-" + programIdCounter);
    }

    oldLinkProgram.apply(gl, [program]);
  };

  const programLinkTimes: { shaderName: string; time: number }[] = [];
  let timer: NodeJS.Timeout | undefined;
  let shaderCount = 0;

  const oldGetProgramParameter = gl.getProgramParameter;
  gl.getProgramParameter = (program: WebGLProgram, pname: GLenum) => {
    const result = oldGetProgramParameter.apply(gl, [program, pname]);
    const info = internalProgramMap.get(program);
    if (info) {
      const { id, shaderName } = info;
      assertIsNotUndefined(shaderName);
      performance.mark("end-compile-webgl-program-" + id);
      const measure = performance.measure(
        "Link " + shaderName,
        "start-compile-webgl-program-" + id,
        "end-compile-webgl-program-" + id,
      );

      programLinkTimes.push({ shaderName, time: measure.duration });
      shaderCount += 1;
      productLog(`Shader #${shaderCount}: ${shaderName} linked in ${measure.duration.toFixed(2)} ms`);
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        productLog("Top Program Link Times:");
        programLinkTimes.sort((a, b) => b.time - a.time);
        for (const { shaderName, time } of programLinkTimes.slice(0, 3)) {
          productLog(`${shaderName}: ${time.toFixed(2)} ms`);
        }
      }, 2000);

      // The below is for debugging slow compiling shader permutations.

      // if (measure.duration > 50) {
      //   const shaders = gl.getAttachedShaders(program);
      //   const sources = shaders?.map((shader) => {
      //     const source = gl.getShaderSource(shader);
      //     const type = gl.getShaderParameter(shader, gl.SHADER_TYPE);
      //     return (type === gl.VERTEX_SHADER ? "vs: " : "fs: ") + source;
      //   });

      //   productLog(sources?.join("/"));
      // }

      internalProgramMap.delete(program);
    }
    return result;
  };

  assertIsNotNull(renderer.info.programs);
  const oldProgramsPush = renderer.info.programs.push;
  renderer.info.programs.push = (...items: THREE.WebGLProgram[]) => {
    for (const program of items) {
      const shaderName = getShaderName(program);
      // @types/three types WebGLProgram.program as `unknown` (its own TODO); at runtime it is the
      // DOM WebGLProgram that keys internalProgramMap.
      const glProgram = program.program as WebGLProgram;
      const info = internalProgramMap.get(glProgram);
      if (info) {
        internalProgramMap.set(glProgram, { id: info.id, shaderName });
      }
    }
    return oldProgramsPush.apply(renderer.info.programs, items);
  };
}

/**
 * Set up three.js renderer settings and customize shaders. Shader customizations are applied to the three.js shader chunks, so all renderers will use them.
 */
export function initTHREEJSRenderer(
  canvas: HTMLCanvasElement,
  glContext: WebGLRenderingContext | WebGL2RenderingContext,
  glContextAttributes: WebGLContextAttributes,
): THREE.WebGLRenderer {
  if (glContext.isContextLost()) {
    throw new Error("WebGL context is lost");
  }

  const renderer = new THREE.WebGLRenderer({
    canvas,
    context: glContext,
    ...glContextAttributes,
  });
  renderer.autoClear = false;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  renderer.shadowMap.enabled = true;
  renderer.shadowMap.autoUpdate = false;
  // Use BasicShadowMap because our custom PCSS implementation (in softShadow.ts) requires
  // sampler2D to read raw depth values. PCFSoftShadowMap is deprecated in Three.js r175+ and
  // PCFShadowMap uses sampler2DShadow with hardware comparison, which is incompatible.
  renderer.shadowMap.type = THREE.BasicShadowMap;

  // The PCSS shadow chunk injected in softShadow.ts hardcodes a standard-depth comparison and
  // ignores the USE_REVERSED_DEPTH_BUFFER branches r185 added upstream, so a reversed depth buffer
  // would invert both the depth test and the bias sign and silently produce wrong shadows. Nothing
  // sets it today, but glContextAttributes is spread into the constructor above, so fail loudly
  // rather than render incorrectly if that ever changes.
  if (renderer.capabilities.reversedDepthBuffer) {
    throw new Error("Reversed depth buffer is incompatible with the PCSS shadows in softShadow.ts");
  }

  enableShaderLogging(renderer, glContext);

  (renderer as ExtendedWebGLRenderer).filteredClippingEnabled = true;

  const originalACESFilmicNominator = "vec3 a = v * ( v + 0.0245786 ) - 0.000090537;";
  const customACESFilmicNominator = "vec3 a = v * v;";

  const originalACESFilmicDivisor = "vec3 b = v * ( 0.983729 * v + 0.4329510 ) + 0.238081;";
  const customACESFilmicDivisor =
    "vec3 b = v * (v * HIGHARC_ACES_FILMIC_DIVISOR_A + HIGHARC_ACES_FILMIC_DIVISOR_B) + HIGHARC_ACES_FILMIC_DIVISOR_C;";

  // Add a factor to the tonemapping curve that's targeted at preventing crunching of dark color values.
  const originalACESFilmicFinal = "return a / b;";
  const customACESFilmicFinal = "return a / b + HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH * v / (v * v + 0.03);";

  // Note: constructor uses row-major order, that's why we transpose these matrices.
  const originalACESFilmicInputMat = new THREE.Matrix3(
    0.59719,
    0.076,
    0.0284,
    0.35458,
    0.90834,
    0.13383,
    0.04823,
    0.01566,
    0.83777,
  ).transpose();

  const originalACESFilmicOutputMat = new THREE.Matrix3(
    1.60475,
    -0.10208,
    -0.00327,
    -0.53108,
    1.10813,
    -0.07276,
    -0.07367,
    -0.00605,
    1.07602,
  ).transpose();

  // This matrix is used to add an extra color space conversion to the ACES Filmic tonemapping so that it can be customized.
  // The aim with tweaking the matrices has been to make the highlights appear a bit warmer colored.
  const customACESTransformMat = new THREE.Matrix3(
    TONEMAPPING_CYAN_HIGHLIGHTS_FACTOR,
    0,
    0,
    0,
    TONEMAPPING_PURPLE_HIGHLIGHTS_FACTOR,
    0,
    0,
    0,
    TONEMAPPING_YELLOW_HIGHLIGHTS_FACTOR,
  );
  const customACESTransformMatInverse = customACESTransformMat.clone().invert();

  // The custom ACES input matrix applies the custom transform, and the output matrix applies its inverse.
  const customACESFilmicInputMat = originalACESFilmicInputMat.clone().premultiply(customACESTransformMat);

  const customACESFilmicOutputMat = originalACESFilmicOutputMat.clone().multiply(customACESTransformMatInverse);

  // Note that three.js build strips comments from shaders, so these matching strings don't include comments.
  const originalACESFilmicInputMatDefinition =
    /const mat3 ACESInputMat = mat3\(\s*vec3\( 0.59719, 0.07600, 0.02840 \),\s*vec3\( 0.35458, 0.90834, 0.13383 \),\s*vec3\( 0.04823, 0.01566, 0.83777 \)\s+\)\;/;
  const customACESFilmicInputMatDefinition = `
  const mat3 ACESInputMat = mat3(
    vec3( ${customACESFilmicInputMat.elements[0]}, ${customACESFilmicInputMat.elements[1]}, ${customACESFilmicInputMat.elements[2]} ),
    vec3( ${customACESFilmicInputMat.elements[3]}, ${customACESFilmicInputMat.elements[4]}, ${customACESFilmicInputMat.elements[5]} ),
    vec3( ${customACESFilmicInputMat.elements[6]}, ${customACESFilmicInputMat.elements[7]}, ${customACESFilmicInputMat.elements[8]} )
  );
  `;

  const originalACESFilmicOutputMatDefinition =
    /const mat3 ACESOutputMat = mat3\(\s*vec3\(  1.60475, -0.10208, -0.00327 \),\s*vec3\( -0.53108,  1.10813, -0.07276 \),\s*vec3\( -0.07367, -0.00605,  1.07602 \)\s+\);/;
  const customACESFilmicOutputMatDefinition = `const mat3 ACESOutputMat = mat3(
    vec3( ${customACESFilmicOutputMat.elements[0]}, ${customACESFilmicOutputMat.elements[1]}, ${customACESFilmicOutputMat.elements[2]} ),
    vec3( ${customACESFilmicOutputMat.elements[3]}, ${customACESFilmicOutputMat.elements[4]}, ${customACESFilmicOutputMat.elements[5]} ),
    vec3( ${customACESFilmicOutputMat.elements[6]}, ${customACESFilmicOutputMat.elements[7]}, ${customACESFilmicOutputMat.elements[8]} )
  );
  `;

  if (!THREE.ShaderChunk.tonemapping_pars_fragment.includes("HIGHARC_ACES_FILMIC_DIVISOR")) {
    THREE.ShaderChunk.tonemapping_pars_fragment = safeShaderReplace(
      THREE.ShaderChunk.tonemapping_pars_fragment,
      originalACESFilmicNominator,
      customACESFilmicNominator,
      "initTHREEJSRenderer tonemapping_pars_fragment (ACES nominator)",
    );
    THREE.ShaderChunk.tonemapping_pars_fragment = safeShaderReplace(
      THREE.ShaderChunk.tonemapping_pars_fragment,
      originalACESFilmicDivisor,
      customACESFilmicDivisor,
      "initTHREEJSRenderer tonemapping_pars_fragment (ACES divisor)",
    );
    THREE.ShaderChunk.tonemapping_pars_fragment = safeShaderReplace(
      THREE.ShaderChunk.tonemapping_pars_fragment,
      originalACESFilmicFinal,
      customACESFilmicFinal,
      "initTHREEJSRenderer tonemapping_pars_fragment (ACES final)",
    );
    THREE.ShaderChunk.tonemapping_pars_fragment = safeShaderReplace(
      THREE.ShaderChunk.tonemapping_pars_fragment,
      originalACESFilmicInputMatDefinition,
      customACESFilmicInputMatDefinition,
      "initTHREEJSRenderer tonemapping_pars_fragment (ACES input mat)",
    );
    THREE.ShaderChunk.tonemapping_pars_fragment = safeShaderReplace(
      THREE.ShaderChunk.tonemapping_pars_fragment,
      originalACESFilmicOutputMatDefinition,
      customACESFilmicOutputMatDefinition,
      "initTHREEJSRenderer tonemapping_pars_fragment (ACES output mat)",
    );
    THREE.ShaderChunk.tonemapping_pars_fragment =
      `
    #define HIGHARC_ACES_FILMIC_DIVISOR 1
    #ifndef HIGHARC_ACES_FILMIC_DIVISOR_A
    #define HIGHARC_ACES_FILMIC_DIVISOR_A 0.9837
    #endif
    #ifndef HIGHARC_ACES_FILMIC_DIVISOR_B
    #define HIGHARC_ACES_FILMIC_DIVISOR_B 0.43
    #endif
    #ifndef HIGHARC_ACES_FILMIC_DIVISOR_C
    #define HIGHARC_ACES_FILMIC_DIVISOR_C 0.24
    #endif
    #ifndef HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH
    #define HIGHARC_ACES_FILMIC_ANTI_DARK_CRUNCH 0.012
    #endif
    ` + THREE.ShaderChunk.tonemapping_pars_fragment;
  }

  if (!THREE.ShaderChunk.lights_fragment_begin.includes("HIGHARC_DISABLE_THREEJS_CORE_LIGHT_PROBE")) {
    // Add option to leave out light probe evaluation from the shader. This should make the shader just a bit faster.
    THREE.ShaderChunk.lights_fragment_begin = safeShaderReplace(
      THREE.ShaderChunk.lights_fragment_begin,
      "irradiance += getLightProbeIrradiance( lightProbe, geometryNormal );",
      `
      #ifndef HIGHARC_DISABLE_THREEJS_CORE_LIGHT_PROBE
        irradiance += getLightProbeIrradiance( lightProbe, geometryNormal );
      #endif
      `,
      "initTHREEJSRenderer lights_fragment_begin (light probe)",
    );
  }

  if (!THREE.ShaderChunk.common.includes("Z_UP")) {
    const commonEquirectUv = `

  #define Z_UP

  vec2 equirectUv( in vec3 dir ) {

    // dir is assumed to be unit length

  #ifdef Z_UP

    float u = atan( -dir.y, dir.x ) * RECIPROCAL_PI2 + 0.5;

    float v = asin( clamp( dir.z, - 1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5;

  #else

    float u = atan( dir.z, dir.x ) * RECIPROCAL_PI2 + 0.5;

    float v = asin( clamp( dir.y, - 1.0, 1.0 ) ) * RECIPROCAL_PI + 0.5;

  #endif

    return vec2( u, v );

  }`;
    THREE.ShaderChunk.common = safeShaderReplace(
      THREE.ShaderChunk.common,
      "vec2 equirectUv( in vec3 dir )",
      commonEquirectUv + "\nvec2 _higharc_equirectUv_replaced( in vec3 dir )",
      "initTHREEJSRenderer common (equirectUv)",
    );
  }

  reorderFogAndColorSpace("meshphysical_frag", THREE.ShaderLib.physical);
  reorderFogAndColorSpace("meshbasic_frag", THREE.ShaderLib.basic);

  return renderer;
}

interface InitPhysicallyBasedRendererParams {
  renderer: THREE.WebGLRenderer;
  capabilities: WebGLExtendedCapabilities;
  userOptions: Partial<RendererInitOptions>;
  markRendererForUpdate?: () => void;
}

export function initPhysicallyBasedRenderer({
  renderer,
  capabilities,
  userOptions,
  markRendererForUpdate,
}: InitPhysicallyBasedRendererParams): InitializedRenderer {
  const options = { ...defaultOptions, ...userOptions };

  // We use light offset maps so point lights are not needed.
  // This mainly guards us against accidentally introducing lights that would adversely affect performance.
  (renderer as ExtendedWebGLRenderer).setFixedMaxSpotLights(0);
  (renderer as ExtendedWebGLRenderer).setFixedMaxPointLights(0);
  (renderer as ExtendedWebGLRenderer).setFixedMaxHemisphereLights(1); // We use one hemisphere light if ssfxRenderer or exterior light probes are disabled.

  let screenRenderTarget: SSFXRenderTarget | undefined;
  let screenLightweightRenderTarget: SSFXLightweightRenderTarget | undefined;
  let ssfxParamsManager: SSFXRendererParamsManager | undefined;
  const renderTargetPool = new RenderTargetPool(renderer);

  if (options.ssfxRenderer) {
    const depthNormalLayerMask = new THREE.Layers();
    depthNormalLayerMask.enableAll();
    depthNormalLayerMask.disable(SITE_CAMERA_FADE_LAYER);
    depthNormalLayerMask.disable(UI_LAYER);
    const defaultRenderParamOverrides: DefaultRenderParamOverrides = {
      depthNormalParams: {
        depthNormalLayerMask,
      },
    };
    if (options.useMSAA) {
      defaultRenderParamOverrides.screenRenderParams = {
        ssaaEnabled: false,
      };
    }

    const canvasSize = new THREE.Vector2();
    renderer.getDrawingBufferSize(canvasSize);

    screenRenderTarget = new SSFXRenderTarget(
      renderer,
      renderTargetPool,
      canvasSize.width,
      canvasSize.height,
      capabilities,
    );
    renderTargetPool.setSize(screenRenderTarget.sampleRenderTargetWidth, screenRenderTarget.sampleRenderTargetHeight);
    renderer.getDrawingBufferSize(canvasSize);
    screenLightweightRenderTarget = new SSFXLightweightRenderTarget(
      renderer,
      canvasSize.width,
      canvasSize.height,
      capabilities,
    );

    const worldScale = 40.0;

    ssfxParamsManager = new SSFXRendererParamsManager(
      markRendererForUpdate ?? noop,
      capabilities,
      worldScale,
      defaultRenderParamOverrides,
    );
  }

  return { renderer, screenRenderTarget, screenLightweightRenderTarget, ssfxParamsManager, renderTargetPool };
}
